import React, { useEffect, useState } from "react"

import { Row, Col, Spinner } from "reactstrap"
import { useSelector, connect } from "react-redux"
import PrimaryButton from "newcomponents/primary-button"

//Import Action to copy breadcrumb items from local state to redux state
import { setBreadcrumbItems } from "../../store/actions"
import SnackbarComponent from "components/SnackBar"
import axios from "axios"
import config from "config/config"
import MiniCards from "newcomponents/mini-cards"
import ReviewBox from "newcomponents/ReviewBox"
import ReviewsSummary from "./ReviewsSummary"
import RatingsTrend from "./RatingsTrend"

const GoogleReviewsDashboard = props => {
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const { organizations, isConsolidatedView } = useSelector(
    state => state.organizationReducer,
  )
  const userData = useSelector(state => state.Login.userData)
  const [report, setReport] = useState([])
  const [reviews, setReviews] = useState([])
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const mode = useSelector(state => state.Layout.layoutMode)

  document.title = "Google Reviews | AeraVue"

  const breadcrumbItems = [
    {
      title: `${activeOrganization?.name}`,
      link: "#",
    },
    { title: "Google Reviews", link: "#" },
  ]

  // Consolidated view spans multiple orgs' Google accounts at once, which
  // doesn't map onto fetchReviews' single-org sync model - that view just
  // reads whatever's already stored (getReviews, DB-only, no live check).
  const fetchStoredReviews = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/reviews/getReviews?integrationIds=${organizations
          .map(org => org.Organization.GoogleReviewsIntegrations[0].id)
          .join(",")}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      setReviews(response.data.reviews)
    } catch (err) {
      console.log(err)
      setErr("Error fetching reviews.")
    }
  }

  // For a single org, this is the "smart" endpoint: it checks Google for
  // anything new if it's been more than 30 minutes since the last sync,
  // otherwise it just serves the already-stored reviews - so a normal page
  // load stays fast, but doesn't go stale forever either. force=true (the
  // "Sync Now" button) bypasses that window for an immediate check.
  const loadReviews = async (force = false) => {
    if (force) setSyncing(true)
    setErr(null)
    try {
      const response = await axios.get(
        `${config.backendUrl}/reviews/fetchReviews?orgId=${activeOrganization.id}&force=${force}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )
      setReviews(response.data.reviews)
      if (force) setSuccess("Reviews synced from Google.")
    } catch (error) {
      setErr(
        error.response?.data?.error || "Error syncing reviews from Google.",
      )
    } finally {
      if (force) setSyncing(false)
    }
  }

  useEffect(() => {
    props.setBreadcrumbItems(
      `${activeOrganization?.name} (${activeOrganization?.type})`,
      breadcrumbItems,
    )

    if (isConsolidatedView) {
      fetchStoredReviews()
    } else {
      loadReviews(false)
    }
  }, [activeOrganization, isConsolidatedView, organizations])

  useEffect(() => {
    // Always populate the stat cards, even with zero reviews (a fresh
    // connection with no reviews yet shouldn't render a blank dashboard) -
    // MiniCards renders nothing for an empty array, so the cards need to
    // exist with zeroed-out values rather than being skipped entirely.
    let count = 0
    let sum = 0
    const positiveReviews = []
    const negativeReviews = []

    for (const review of reviews) {
      count++
      sum += parseFloat(review.rating || 0)

      if (review.rating >= 4) {
        positiveReviews.push(review)
      } else {
        negativeReviews.push(review)
      }
    }

    const averageRating = count > 0 ? (sum / count).toFixed(1) : "0.0"
    const positivePct = count > 0 ? ((positiveReviews.length / count) * 100).toFixed(1) : "0.0"
    const negativePct = count > 0 ? ((negativeReviews.length / count) * 100).toFixed(1) : "0.0"

    setReport([
      {
        title: "Total Reviews",
        iconClass: "message-draw",
        total: count,
        average: count > 10 ? "+15%" : "-5%",
        badgecolor: count > 10 ? "success" : "danger",
      },
      {
        title: "Average Rating",
        iconClass: "star",
        total: averageRating,
        average: "Stable",
        badgecolor: "warning",
      },
      {
        title: "Positive Reviews",
        iconClass: "thumb-up",
        total: positiveReviews.length,
        average: `${positivePct}%`,
        badgecolor: "success",
      },
      {
        title: "Negative Reviews",
        iconClass: "thumb-down",
        total: negativeReviews.length,
        average: `${negativePct}%`,
        badgecolor: "danger",
      },
    ])
  }, [reviews, isConsolidatedView, organizations, activeOrganization])

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">Google Reviews</h4>
        </Col>
        <Col className="text-end">
          <PrimaryButton
            style={{ padding: "10px 30px", borderRadius: "8px" }}
            onClick={() => loadReviews(true)}
            disabled={syncing || isConsolidatedView}
            title={
              isConsolidatedView
                ? "Switch to a single organization to sync"
                : ""
            }
          >
            {syncing ? <Spinner size="sm" /> : "Sync Now"}
          </PrimaryButton>
        </Col>
      </Row>

      <MiniCards reports={report} mode={mode} />

      <Row>
        <Col xl="6">
          <ReviewsSummary reviews={reviews} />
        </Col>
        <Col xl="6">
          <RatingsTrend reviews={reviews} />
        </Col>
      </Row>

      <Row>
        <ReviewBox reviews={reviews} mode={mode} platform="reviews" />
      </Row>
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(GoogleReviewsDashboard)
